import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">分类不存在</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium text-sm cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `https://jiuqugan.com/category/${slug}`;

  return (
    <>
      <SEOHead
        title={`${category.name} - 就去干行动力觉醒`}
        description={category.description}
        keywords={`${category.name},就去干,行动力,拖延症,执行力,${category.name}方法`}
        canonicalUrl={canonicalUrl}
        ogType="website"
      />

      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />

            <div className="py-8 md:py-12">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className={`${category.icon} text-xl`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                    {category.name}
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">
                    {category.description}
                  </p>
                </div>
              </div>

              <p className="text-sm text-foreground-400">
                共 {categoryArticles.length} 篇文章
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 pb-16 md:pb-24">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group flex flex-col bg-background-50 rounded-lg border border-background-200/80 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full h-48 md:h-52 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5 md:p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs text-foreground-400 mb-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line w-3 h-3 flex items-center justify-center"></i>
                        {article.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line w-3 h-3 flex items-center justify-center"></i>
                        {article.readTime}
                      </span>
                    </div>
                    <h2 className="text-sm md:text-base font-heading font-bold text-foreground-950 mb-3 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                      {article.title}
                    </h2>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-3 flex-1">
                      {article.excerpt}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {article.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {categoryArticles.length === 0 && (
              <div className="pb-16 md:pb-24 text-center py-20">
                <p className="text-foreground-500 text-sm">该分类暂无文章，敬请期待。</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}